import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  MagacinReceiving,
  MagacinReceivingItem,
  MagacinReceivingStatus,
  MagacinReceivingIssueType,
  MagacinReceivingType
} from '../types';
import { magacinReceivingsAPI } from '../services/api';
import './Magacin.css';

const useIsMobile = (breakpoint: number = 768) => {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

const fmtDate = (iso?: string) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('sr-RS');
  } catch {
    return iso;
  }
};

const fmtDateTime = (iso?: string) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('sr-RS');
  } catch {
    return iso;
  }
};

const statusTag = (status: MagacinReceivingStatus) => {
  if (status === 'pending') return <Tag value="Na čekanju" severity="warning" icon="pi pi-clock" />;
  if (status === 'approved') return <Tag value="Odobreno" severity="success" icon="pi pi-check" />;
  if (status === 'rejected') return <Tag value="Odbijeno" severity="danger" icon="pi pi-times" />;
  return <Tag value={status} />;
};

const receivingTypeLabel = (t?: MagacinReceivingType): string => {
  if (t === 'sirovina') return 'Sirovina';
  if (t === 'pakovni') return 'Pakovni materijal';
  return 'Rezervni deo';
};

const receivingTypeTag = (t?: MagacinReceivingType) => {
  if (t === 'sirovina')
    return <Tag value="Sirovina" severity="info" icon="pi pi-th-large" />;
  if (t === 'pakovni')
    return <Tag value="Pakovni materijal" severity="warning" icon="pi pi-box" />;
  return <Tag value="Rezervni deo" severity="success" icon="pi pi-wrench" />;
};

const issueTypeLabel = (t: MagacinReceivingIssueType) => {
  switch (t) {
    case 'missing':
      return 'Manjak';
    case 'damaged':
      return 'Oštećenje';
    case 'wrong':
      return 'Pogrešno';
    default:
      return 'Ostalo';
  }
};

const issueTypeOptions: Array<{ label: string; value: MagacinReceivingIssueType }> = [
  { label: 'Manjak', value: 'missing' },
  { label: 'Oštećenje', value: 'damaged' },
  { label: 'Pogrešno', value: 'wrong' },
  { label: 'Ostalo', value: 'other' }
];

const userName = (u: any): string => {
  if (!u) return '-';
  if (typeof u === 'string') return u;
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || '-';
};

const MagacinReceivings: React.FC = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const isKontrola = user?.role === 'magacin_kontrola';
  const isAdminLike = user?.role === 'admin' || user?.role === 'direktor';

  // URL-based scope: /logistika/receivings vs /magacin/receivings.
  // Kontrolor uses the page as a unified "my otpremnice" inbox regardless of scope.
  const urlScope: 'magacin' | 'logistika' = location.pathname.startsWith('/logistika') ? 'logistika' : 'magacin';
  const scopeTypes: MagacinReceivingType[] | null = isKontrola
    ? null
    : urlScope === 'logistika'
      ? ['sirovina', 'pakovni']
      : ['rezervni_deo'];

  const scopeTitle: string = isKontrola
    ? 'Moje otpremnice'
    : urlScope === 'logistika'
      ? 'Ulaz u logistiku – Otpremnice'
      : 'Ulaz u magacin – Otpremnice';

  const scopeSubtitle: string = isKontrola
    ? 'Pratite status vaših otpremnica. Kreirajte nove otpremnice za stavke koje su pristigle.'
    : urlScope === 'logistika'
      ? 'Otpremnice za sirovine i pakovni materijal. Odobravanjem se materijali dodaju na stanje logistike.'
      : 'Otpremnice za rezervne delove. Odobravanjem se proizvodi dodaju na stanje magacina.';

  const canActOnType = (row: MagacinReceiving): boolean => {
    if (isAdminLike) return true;
    if (row.receivingType === 'rezervni_deo') return user?.role === 'magacin';
    if (row.receivingType === 'sirovina' || row.receivingType === 'pakovni')
      return user?.role === 'logistika';
    return false;
  };

  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<MagacinReceiving[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  const [detail, setDetail] = useState<MagacinReceiving | null>(null);

  const [rejectVisible, setRejectVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveVisible, setApproveVisible] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');

  const [issueVisible, setIssueVisible] = useState(false);
  const [newIssue, setNewIssue] = useState({
    type: 'damaged' as MagacinReceivingIssueType,
    productName: '',
    quantity: 0,
    unit: '',
    description: ''
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await magacinReceivingsAPI.getAll(
        scopeTypes ? { receivingType: scopeTypes } : undefined
      );
      setList(res);
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri učitavanju otpremnica',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // re-load when URL scope changes (magacin ↔ logistika)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlScope]);

  const filteredByStatus = useMemo(() => {
    const buckets: Record<MagacinReceivingStatus, MagacinReceiving[]> = {
      pending: [],
      approved: [],
      rejected: []
    };
    for (const r of list) {
      buckets[r.status]?.push(r);
    }
    return buckets;
  }, [list]);

  const refreshDetail = (updated: MagacinReceiving) => {
    setList((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
    if (detail && detail._id === updated._id) {
      setDetail(updated);
    }
    try {
      window.dispatchEvent(new Event('receivings-updated'));
    } catch {
      // no-op
    }
  };

  const onApprove = async () => {
    if (!detail) return;
    setLoading(true);
    try {
      const updated = await magacinReceivingsAPI.approve(detail._id, approveNotes.trim());
      refreshDetail(updated);
      toast.current?.show({
        severity: 'success',
        summary: 'Odobreno',
        detail: `Otpremnica ${updated.receivingNumber} je odobrena. Stavke su prijavljene na stanje ${updated.receivingType === 'rezervni_deo' ? 'magacina' : 'logistike'}.`,
        life: 3500
      });
      setApproveVisible(false);
      setApproveNotes('');
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri odobravanju',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const onReject = async () => {
    if (!detail) return;
    if (!rejectReason.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Razlog odbijanja je obavezan', life: 3000 });
      return;
    }
    setLoading(true);
    try {
      const updated = await magacinReceivingsAPI.reject(detail._id, rejectReason.trim());
      refreshDetail(updated);
      toast.current?.show({
        severity: 'info',
        summary: 'Odbijeno',
        detail: `Otpremnica ${updated.receivingNumber} je odbijena.`,
        life: 3000
      });
      setRejectVisible(false);
      setRejectReason('');
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri odbijanju',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const onAddIssue = async () => {
    if (!detail) return;
    if (!newIssue.description.trim()) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Opis je obavezan', life: 3000 });
      return;
    }
    setLoading(true);
    try {
      const updated = await magacinReceivingsAPI.addIssue(detail._id, {
        type: newIssue.type,
        productName: newIssue.productName.trim(),
        quantity: newIssue.quantity || 0,
        unit: newIssue.unit.trim(),
        description: newIssue.description.trim()
      });
      refreshDetail(updated);
      toast.current?.show({
        severity: 'success',
        summary: 'Dodato',
        detail: 'Izveštaj je dodat',
        life: 2500
      });
      setIssueVisible(false);
      setNewIssue({ type: 'damaged', productName: '', quantity: 0, unit: '', description: '' });
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri dodavanju izveštaja',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const onRemoveIssue = async (issueId?: string) => {
    if (!detail || !issueId) return;
    confirmDialog({
      message: 'Sigurno želite da uklonite ovaj izveštaj?',
      header: 'Brisanje',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Da, ukloni',
      rejectLabel: 'Otkaži',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          const updated = await magacinReceivingsAPI.removeIssue(detail._id, issueId);
          refreshDetail(updated);
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri brisanju izveštaja',
            life: 5000
          });
        }
      }
    });
  };

  const onDeleteReceiving = (rec: MagacinReceiving) => {
    confirmDialog({
      message: `Sigurno želite da obrišete otpremnicu ${rec.receivingNumber}?`,
      header: 'Brisanje',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Da, obriši',
      rejectLabel: 'Otkaži',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await magacinReceivingsAPI.delete(rec._id);
          setList((prev) => prev.filter((r) => r._id !== rec._id));
          if (detail && detail._id === rec._id) {
            setDetail(null);
          }
          try {
            window.dispatchEvent(new Event('receivings-updated'));
          } catch {
            // no-op
          }
          toast.current?.show({ severity: 'success', summary: 'Obrisano', detail: 'Otpremnica je obrisana', life: 2500 });
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri brisanju',
            life: 5000
          });
        }
      }
    });
  };

  const canDeleteRow = (row: MagacinReceiving) =>
    canActOnType(row) ||
    (row.status === 'pending' &&
      isKontrola &&
      String((row.createdBy as any)?._id || row.createdBy) === String(user?._id));

  const renderMobileCards = (data: MagacinReceiving[]) => {
    if (!data.length) {
      return <div className="receiving-cards-empty">Nema otpremnica.</div>;
    }
    return (
      <div className="receiving-cards-list">
        {data.map((row) => (
          <div key={row._id} className="receiving-card" onClick={() => setDetail(row)} role="button" tabIndex={0}>
            <div className="receiving-card-top">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="receiving-card-number">{row.receivingNumber}</div>
                <div className="receiving-card-title">{row.deliveryNoteName}</div>
                <div className="mt-1">{receivingTypeTag(row.receivingType)}</div>
              </div>
              {statusTag(row.status)}
            </div>
            <div className="receiving-card-meta">
              <span>
                Stavki: <strong>{row.items?.length || 0}</strong>
              </span>
              <span>
                Izveštaja: <strong>{row.issues?.length || 0}</strong>
              </span>
              <span>{fmtDate(row.createdAt)}</span>
              <span>{userName(row.createdBy)}</span>
            </div>
            <div className="receiving-card-actions" onClick={(e) => e.stopPropagation()}>
              <Button
                label="Detalji"
                icon="pi pi-eye"
                size="small"
                outlined
                onClick={() => setDetail(row)}
              />
              {canDeleteRow(row) && (
                <Button
                  label="Obriši"
                  icon="pi pi-trash"
                  severity="danger"
                  size="small"
                  outlined
                  onClick={() => onDeleteReceiving(row)}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderDesktopTable = (data: MagacinReceiving[]) => (
    <DataTable
      value={data}
      loading={loading}
      paginator
      rows={15}
      rowsPerPageOptions={[10, 15, 25, 50]}
      stripedRows
      emptyMessage="Nema otpremnica."
      responsiveLayout="scroll"
    >
      <Column field="receivingNumber" header="Broj" sortable />
      <Column field="deliveryNoteName" header="Naziv otpremnice" sortable />
      <Column
        header="Tip"
        body={(row: MagacinReceiving) => receivingTypeTag(row.receivingType)}
        style={{ width: 170 }}
      />
      <Column
        header="Stavki"
        body={(row: MagacinReceiving) => row.items?.length || 0}
        style={{ width: 90 }}
      />
      <Column
        header="Izveštaja"
        body={(row: MagacinReceiving) => row.issues?.length || 0}
        style={{ width: 110 }}
      />
      <Column header="Status" body={(row: MagacinReceiving) => statusTag(row.status)} style={{ width: 140 }} />
      <Column
        header="Kreirao"
        body={(row: MagacinReceiving) => userName(row.createdBy)}
        style={{ minWidth: 160 }}
      />
      <Column header="Datum" body={(row: MagacinReceiving) => fmtDateTime(row.createdAt)} style={{ width: 170 }} />
      <Column
        header="Akcije"
        style={{ width: 220 }}
        body={(row: MagacinReceiving) => (
          <div className="flex gap-2">
            <Button icon="pi pi-eye" rounded outlined size="small" tooltip="Detalji" onClick={() => setDetail(row)} />
            {canDeleteRow(row) && (
              <Button
                icon="pi pi-trash"
                rounded
                outlined
                severity="danger"
                size="small"
                tooltip={canActOnType(row) ? 'Obriši' : 'Obriši (samo dok je na čekanju)'}
                onClick={() => onDeleteReceiving(row)}
              />
            )}
          </div>
        )}
      />
    </DataTable>
  );

  const renderTable = (data: MagacinReceiving[]) =>
    isMobile ? renderMobileCards(data) : renderDesktopTable(data);

  const headerActions = (
    <div className="flex gap-2">
      <Button
        label={isMobile ? '' : 'Osveži'}
        icon="pi pi-refresh"
        severity="secondary"
        onClick={load}
        loading={loading}
        tooltip={isMobile ? 'Osveži' : undefined}
      />
      {(isKontrola || isAdminLike) && (
        <Button
          label={isMobile ? 'Nova' : 'Nova otpremnica'}
          icon="pi pi-plus"
          severity="info"
          onClick={() =>
            navigate(urlScope === 'logistika' ? '/logistika/receivings/create' : '/magacin/receivings/create')
          }
        />
      )}
    </div>
  );

  const detailVisible = !!detail;

  return (
    <div className="magacin-page p-3 md:p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="magacin-header flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <h2 className="m-0">{scopeTitle}</h2>
          {!isMobile && <div className="text-600 mt-1">{scopeSubtitle}</div>}
        </div>
        {headerActions}
      </div>

      {isMobile ? (
        <>
          <Dropdown
            value={activeTab}
            onChange={(e) => setActiveTab(e.value)}
            options={[
              {
                value: 0,
                label: `Na čekanju (${filteredByStatus.pending.length})`,
                icon: 'pi pi-clock'
              },
              {
                value: 1,
                label: `Odobreno (${filteredByStatus.approved.length})`,
                icon: 'pi pi-check'
              },
              {
                value: 2,
                label: `Odbijeno (${filteredByStatus.rejected.length})`,
                icon: 'pi pi-times'
              },
              {
                value: 3,
                label: `Sve (${list.length})`,
                icon: 'pi pi-list'
              }
            ]}
            className="w-full mb-3 receivings-status-dropdown"
            valueTemplate={(option: any) =>
              option ? (
                <span className="flex align-items-center gap-2">
                  <i className={option.icon} />
                  <span>{option.label}</span>
                </span>
              ) : (
                <span>Izaberi status</span>
              )
            }
            itemTemplate={(option: any) => (
              <span className="flex align-items-center gap-2">
                <i className={option.icon} />
                <span>{option.label}</span>
              </span>
            )}
          />
          <div>
            {activeTab === 0 && renderTable(filteredByStatus.pending)}
            {activeTab === 1 && renderTable(filteredByStatus.approved)}
            {activeTab === 2 && renderTable(filteredByStatus.rejected)}
            {activeTab === 3 && renderTable(list)}
          </div>
        </>
      ) : (
        <Card>
          <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
            <TabPanel header={`Na čekanju (${filteredByStatus.pending.length})`} leftIcon="pi pi-clock mr-2">
              {renderTable(filteredByStatus.pending)}
            </TabPanel>
            <TabPanel header={`Odobreno (${filteredByStatus.approved.length})`} leftIcon="pi pi-check mr-2">
              {renderTable(filteredByStatus.approved)}
            </TabPanel>
            <TabPanel header={`Odbijeno (${filteredByStatus.rejected.length})`} leftIcon="pi pi-times mr-2">
              {renderTable(filteredByStatus.rejected)}
            </TabPanel>
            <TabPanel header={`Sve (${list.length})`} leftIcon="pi pi-list mr-2">
              {renderTable(list)}
            </TabPanel>
          </TabView>
        </Card>
      )}

      <Dialog
        visible={detailVisible}
        onHide={() => setDetail(null)}
        header={detail ? `Otpremnica ${detail.receivingNumber}` : ''}
        style={{ width: '92vw', maxWidth: 1100 }}
        breakpoints={{ '768px': '100vw' }}
        maximizable={!isMobile}
        modal
      >
        {detail && (
          <div className="grid">
            <div className="col-12 md:col-8">
              <div className="text-sm text-600">Naziv otpremnice</div>
              <div className="font-semibold mb-2">{detail.deliveryNoteName}</div>
              {detail.notes && (
                <>
                  <div className="text-sm text-600">Napomena</div>
                  <div className="mb-2">{detail.notes}</div>
                </>
              )}
            </div>
            <div className={`col-12 md:col-4 ${isMobile ? '' : 'text-right'}`}>
              <div className="mb-2 flex md:justify-content-end gap-2 flex-wrap">
                {receivingTypeTag(detail.receivingType)}
                {statusTag(detail.status)}
              </div>
              <div className="text-sm text-600">Kreirao: {userName(detail.createdBy)}</div>
              <div className="text-sm text-600">Datum: {fmtDateTime(detail.createdAt)}</div>
              {detail.reviewedBy && (
                <>
                  <div className="text-sm text-600 mt-2">Pregledao: {userName(detail.reviewedBy)}</div>
                  <div className="text-sm text-600">Pregled: {fmtDateTime(detail.reviewedAt)}</div>
                </>
              )}
            </div>

            {detail.status === 'rejected' && detail.rejectionReason && (
              <div className="col-12">
                <div className="p-3 surface-100 border-round border-1 border-red-200">
                  <div className="font-semibold text-red-700 mb-1">Razlog odbijanja</div>
                  <div>{detail.rejectionReason}</div>
                </div>
              </div>
            )}

            {detail.status === 'approved' && detail.approvalNotes && (
              <div className="col-12">
                <div className="p-3 surface-100 border-round border-1 border-green-200">
                  <div className="font-semibold text-green-700 mb-1">Napomena pri odobravanju</div>
                  <div>{detail.approvalNotes}</div>
                </div>
              </div>
            )}

            <div className="col-12">
              <h4 className="mb-2">Stavke</h4>
              <DataTable
                value={detail.items || []}
                stripedRows
                emptyMessage="Nema stavki."
                responsiveLayout="stack"
                breakpoint="768px"
                className="p-datatable-sm"
              >
                <Column header="Kategorija" field="categoryName" />
                <Column header="Proizvod" field="productName" />
                <Column
                  header="Količina"
                  body={(it: MagacinReceivingItem) => `${it.quantity} ${it.unit || ''}`.trim()}
                />
                <Column header="Datum dolaska" body={(it: MagacinReceivingItem) => fmtDate(it.arrivalDate)} />
                <Column header="Dobavljač" field="supplier" />
                <Column header="Broj serije" field="batchNumber" />
                <Column header="Napomena" field="notes" />
              </DataTable>
            </div>

            <div className="col-12 mt-3">
              <div className="flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <h4 className="m-0">Izveštaji</h4>
                <Button
                  label={isMobile ? 'Dodaj' : 'Dodaj izveštaj'}
                  icon="pi pi-flag"
                  severity="warning"
                  size="small"
                  onClick={() => setIssueVisible(true)}
                />
              </div>
              <DataTable
                value={detail.issues || []}
                stripedRows
                emptyMessage="Nema unetih izveštaja."
                responsiveLayout="stack"
                breakpoint="768px"
                className="p-datatable-sm"
              >
                <Column header="Tip" body={(iss: any) => issueTypeLabel(iss.type)} style={{ width: 140 }} />
                <Column header="Proizvod" field="productName" />
                <Column
                  header="Količina"
                  body={(iss: any) => (iss.quantity ? `${iss.quantity} ${iss.unit || ''}`.trim() : '-')}
                  style={{ width: 130 }}
                />
                <Column header="Opis" field="description" />
                <Column
                  header="Prijavio"
                  body={(iss: any) => userName(iss.reportedBy)}
                  style={{ width: 160 }}
                />
                <Column header="Kada" body={(iss: any) => fmtDateTime(iss.reportedAt)} style={{ width: 170 }} />
                <Column
                  header=""
                  style={{ width: 60 }}
                  body={(iss: any) => (
                    <Button
                      icon="pi pi-trash"
                      rounded
                      text
                      severity="danger"
                      size="small"
                      onClick={() => onRemoveIssue(iss._id)}
                    />
                  )}
                />
              </DataTable>
            </div>

            {canActOnType(detail) && detail.status === 'pending' && (
              <div className="col-12 flex justify-content-end gap-2 mt-3 flex-column md:flex-row">
                <Button
                  label="Odbij"
                  icon="pi pi-times"
                  severity="danger"
                  outlined
                  onClick={() => setRejectVisible(true)}
                  className="w-full md:w-auto"
                />
                <Button
                  label={isMobile ? 'Odobri i na stanje' : 'Odobri i prijavi na stanje'}
                  icon="pi pi-check"
                  severity="success"
                  onClick={() => setApproveVisible(true)}
                  className="w-full md:w-auto"
                />
              </div>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        visible={approveVisible}
        onHide={() => setApproveVisible(false)}
        header="Odobravanje otpremnice"
        style={{ width: '90vw', maxWidth: 600 }}
        modal
      >
        <div className="p-fluid">
          <p className="mt-0">
            {detail?.receivingType === 'rezervni_deo'
              ? 'Sve stavke će biti prijavljene na stanje magacina. Postojeći proizvodi će biti uvećani, a nepostojeći kreirani.'
              : `Sve stavke će biti dodate na stanje logistike (${receivingTypeLabel(detail?.receivingType)}). Količina će biti uvećana za postojeće materijale.`}
          </p>
          <div className="field">
            <label className="block mb-2">Napomena (opciono)</label>
            <InputTextarea
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              rows={3}
              autoResize
              className="w-full"
            />
          </div>
          <div className="flex justify-content-end gap-2 mt-3">
            <Button label="Otkaži" severity="secondary" outlined onClick={() => setApproveVisible(false)} />
            <Button label="Odobri" icon="pi pi-check" severity="success" loading={loading} onClick={onApprove} />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={rejectVisible}
        onHide={() => setRejectVisible(false)}
        header="Odbijanje otpremnice"
        style={{ width: '90vw', maxWidth: 600 }}
        modal
      >
        <div className="p-fluid">
          <div className="field">
            <label className="block mb-2">Razlog odbijanja *</label>
            <InputTextarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              autoResize
              className="w-full"
            />
          </div>
          <div className="flex justify-content-end gap-2 mt-3">
            <Button label="Otkaži" severity="secondary" outlined onClick={() => setRejectVisible(false)} />
            <Button label="Odbij" icon="pi pi-times" severity="danger" loading={loading} onClick={onReject} />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={issueVisible}
        onHide={() => setIssueVisible(false)}
        header="Novi izveštaj"
        style={{ width: '90vw', maxWidth: 600 }}
        modal
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-4">
            <label>Tip *</label>
            <Dropdown
              value={newIssue.type}
              options={issueTypeOptions}
              onChange={(e) => setNewIssue({ ...newIssue, type: e.value })}
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-8">
            <label>Naziv proizvoda</label>
            <InputText
              value={newIssue.productName}
              onChange={(e) => setNewIssue({ ...newIssue, productName: e.target.value })}
            />
          </div>
          <div className="field col-6">
            <label>Količina</label>
            <InputNumber
              value={newIssue.quantity}
              onValueChange={(e) => setNewIssue({ ...newIssue, quantity: e.value ?? 0 })}
              min={0}
            />
          </div>
          <div className="field col-6">
            <label>Jedinica</label>
            <InputText
              value={newIssue.unit}
              onChange={(e) => setNewIssue({ ...newIssue, unit: e.target.value })}
            />
          </div>
          <div className="field col-12">
            <label>Opis *</label>
            <InputTextarea
              value={newIssue.description}
              onChange={(e) => setNewIssue({ ...newIssue, description: e.target.value })}
              rows={3}
              autoResize
            />
          </div>
        </div>
        <div className="flex justify-content-end gap-2">
          <Button label="Otkaži" severity="secondary" outlined onClick={() => setIssueVisible(false)} />
          <Button label="Sačuvaj" icon="pi pi-check" severity="info" loading={loading} onClick={onAddIssue} />
        </div>
      </Dialog>
    </div>
  );
};

export default MagacinReceivings;
